import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE, getToken } from "../../lib/auth";

type Inquiry = {
  id: number;
  user_id?: number | null;
  name: string;
  email: string;
  category: string;
  content: string;
  status: "open" | "answered" | "closed" | string;
  admin_reply?: string | null;
  created_at: string;
  replied_at?: string | null;
};

const CAT_KO: Record<string, string> = {
  general: "서비스 문의",
  payment: "결제 관련",
  contract: "계약/서류",
  account: "계정/인증",
  tech: "오류 신고",
  etc: "기타",
};

const STATUS_OPTIONS: Array<{ value: "open" | "answered"; label: string }> = [
  { value: "open", label: "대기" },
  { value: "answered", label: "답변완료" },
];

function formatCategory(v?: string | null) {
  if (!v) return "-";
  const key = String(v).toLowerCase();
  return CAT_KO[key] || v;
}

function formatDate(s?: string | null) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function normalizeStatus(raw?: string | null): "open" | "answered" {
  const v = String(raw || "open").toLowerCase();
  if (v === "answered") return "answered";
  return "open";
}

export default function AdminInquiryView() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const [item, setItem] = useState<Inquiry | null>(null);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState<"open" | "answered">("open");
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const canSave = useMemo(() => !!reply.trim() && !saving, [reply, saving]);

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/inquiries/admin/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) throw new Error(j?.message || "상세 조회 실패");

      const inquiry: Inquiry = j.inquiry;
      setItem(inquiry);

      setStatus(normalizeStatus(inquiry.status));
      setReply(inquiry.admin_reply || "");
    } catch (e) {
      console.error(e);
      alert("문의 상세 정보를 불러오지 못했습니다.");
      setItem(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function saveReply() {
    if (!id) return;
    if (!reply.trim()) {
      alert("답변을 입력해 주세요.");
      return;
    }

    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/inquiries/admin/${id}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ reply: reply.trim(), status }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) throw new Error(j?.message || "답변 저장 실패");

      alert("답변이 저장되었습니다.");
      await load();
    } catch (e: any) {
      console.error(e);
      alert(e?.message || "답변 저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* 헤더: 모바일 스택 */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">
          1:1 문의 상세 {id ? `#${id}` : ""}
        </h2>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={() => nav(-1)}
            className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm hover:bg-slate-50 sm:w-auto"
          >
            목록
          </button>
          <button
            onClick={load}
            className="h-10 w-full rounded-lg bg-slate-900 px-3 text-sm text-white hover:brightness-110 sm:w-auto"
          >
            새로고침
          </button>
        </div>
      </div>

      {/* 기본 정보 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          <div className="space-y-1">
            <div className="text-xs text-slate-500">상태</div>
            <select
              className="h-10 w-full rounded border border-slate-200 px-2 text-sm outline-none focus:border-slate-400 sm:w-44"
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-slate-500">문의 유형</div>
            <div className="text-sm">{formatCategory(item?.category)}</div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-slate-500">등록일자</div>
            <div className="text-sm">{formatDate(item?.created_at)}</div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-slate-500">답변일자</div>
            <div className="text-sm">{formatDate(item?.replied_at)}</div>
          </div>
        </div>
      </section>

      {/* 신청자 정보 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">
          신청자 정보
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          <div className="space-y-1">
            <div className="text-xs text-slate-500">이름</div>
            <div className="text-sm">{item?.name || "-"}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">이메일</div>
            <div className="text-sm break-all">{item?.email || "-"}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">회원 ID</div>
            <div className="text-sm">
              {item?.user_id != null ? item.user_id : "-"}
            </div>
          </div>
        </div>
      </section>

      {/* 문의 내용 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">문의 내용</h3>
        <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-800">
          {item?.content || "-"}
        </div>
      </section>

      {/* 관리자 답변 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">
          관리자 답변
        </h3>
        <textarea
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-400"
          rows={6}
          placeholder="답변 내용을 입력해 주세요."
          value={reply}
          onChange={(e) => setReply(e.target.value)}
        />

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={saveReply}
            disabled={!canSave}
            className={[
              "h-11 rounded-lg px-5 text-sm font-medium text-white shadow-sm",
              canSave ? "bg-sky-600 hover:bg-sky-500" : "cursor-not-allowed bg-slate-300",
            ].join(" ")}
          >
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </section>

      {loading && <div className="text-center text-slate-500">불러오는 중…</div>}
    </div>
  );
}
